import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as EyeIconSlash } from '../../assets/icons/eye-slash.svg';

type InputType = 'text' | 'number' | 'email' | 'password';

interface CustomInputProperties {
  inputLabel?: string;
  type: InputType;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue?: (name: string, value: string | number) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  svgIcon?: JSX.Element;
  frontSvgIcon?: JSX.Element;
  svgIconClickFunc?: () => void;
  error?: boolean;
  errorMessage?: string;
  handleBlur?: (name: string) => void;
  autoFocus?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

/**
 * Custom Input box function component
 */
function CustomInput({
  inputLabel,
  type,
  name,
  placeholder,
  value,
  onChangeValue = () => {},
  valueRequired,
  readOnly,
  svgIcon,
  frontSvgIcon,
  svgIconClickFunc,
  error,
  errorMessage,
  handleBlur,
  autoFocus,
  fontSize = 'large',
  disabled,
}: CustomInputProperties) {
  const { t } = useTranslation('sharedComponent');
  const inputReference = useRef<HTMLInputElement>(null);
  const [inputType, setInputType] = useState<InputType>(type);
  /**
   * Input box change handle function
   */
  const handleValueChange = (changeValue: string | number) => {
    onChangeValue(name, changeValue);
  };

  /**
   * Input's start and end white space trim helper
   * @param event
   */
  const internalHandleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const rawValue = event.target.value;

    // Trim start & end only
    const trimmedValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    // If different, update parent
    if (trimmedValue !== rawValue) {
      onChangeValue(name, trimmedValue);
    }

    // Call parent blur callback if provided
    if (handleBlur) {
      handleBlur(name);
    }
  };

  useEffect(() => {
    if (autoFocus && inputReference.current) {
      inputReference.current.focus(); // Focus the input field if condition is true
    }
  }, [autoFocus]);

  return (
    <div className={`custom-input-wrap ${fontSize}`}>
      {inputLabel && (
        <p className="input-label">
          {t(inputLabel)}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-input-container ${error && 'error'}`}>
        {frontSvgIcon && <span>{frontSvgIcon}</span>}
        <input
          type={type === 'password' ? inputType : type}
          placeholder={t(placeholder)}
          value={value !== null && value !== undefined ? value : ''}
          onChange={(event) => handleValueChange(event.target.value)}
          required={valueRequired}
          readOnly={readOnly}
          onBlur={internalHandleBlur}
          disabled={disabled}
          onWheel={(event) => event.currentTarget.blur()}
        />
        {svgIcon && type !== 'password' && (
          <span role="presentation" onClick={svgIconClickFunc}>
            {svgIcon}
          </span>
        )}
        {(() => {
          let passwordIcon: JSX.Element | null = null; // Initialize the type as either a JSX element or null
          if (type === 'password' && inputType === 'password') {
            passwordIcon = (
              <span role="presentation" onClick={() => setInputType('text')}>
                <EyeIcon />
              </span>
            );
          } else if (type === 'password' && inputType === 'text') {
            passwordIcon = (
              <span role="presentation" onClick={() => setInputType('password')}>
                <EyeIconSlash />
              </span>
            );
          }
          return passwordIcon; // Return the icon or null
        })()}
      </div>
      {error && <p className="text-danger">{errorMessage}</p>}
    </div>
  );
}

CustomInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  svgIcon: null,
  frontSvgIcon: null,
  error: false,
  errorMessage: '',
  handleBlur: null,
  autoFocus: false,
  fontSize: 'large',
  disabled: false,
  /**
   * Icon click function
   */
  svgIconClickFunc: () => {},
  /**
   * Input change function
   */
  onChangeValue: () => {},
};

export default CustomInput;
